import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { API_BASE_URL } from '../config/api';
import { createOptimizedObserver } from '../utils/layoutOptimizer';

const FastImage = ({ 
  src, 
  alt, 
  sx = {},
  aspectRatio = '1',
  priority = false,  // For above-the-fold images
  sizes = '(max-width: 768px) 100vw, 50vw',  // Responsive sizing
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(priority); // Load immediately if priority
  const imgRef = useRef();
  const observerRef = useRef();

  // Memoize the image URL to prevent recalculation
  const imageUrl = useMemo(() => {
    if (!src || src === '/placeholder-image.jpg') {
      return src;
    }

    // For external URLs, return as-is
    if (src.startsWith('http') && !src.includes(API_BASE_URL)) {
      return src;
    }

    // For local images, use direct path
    let imagePath = src;
    
    // Clean up the path
    if (imagePath.startsWith(API_BASE_URL)) {
      imagePath = imagePath.replace(API_BASE_URL + '/', '');
    }
    
    if (imagePath.startsWith('/')) {
      imagePath = imagePath.substring(1);
    }

    return `${API_BASE_URL}/${imagePath}`;
  }, [src]);

  // Intersection Observer for lazy loading (only if not priority)
  useEffect(() => {
    if (priority || inView) return;

    const observer = createOptimizedObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before the image comes into view
        threshold: 0.1
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [priority, inView]);

  // Preload the image when it comes into view
  useEffect(() => {
    if (!inView || !imageUrl || isLoaded) return;

    const img = new Image();
    
    // Set up event handlers before setting src
    img.onload = () => {
      setIsLoaded(true);
      setError(false);
    };
    
    img.onerror = () => {
      setError(true);
      setIsLoaded(true);
    };

    // Add size hints for better performance
    if (sizes) {
      img.sizes = sizes;
    }

    // Start loading
    img.src = imageUrl;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [inView, imageUrl, isLoaded, sizes]);

  // Render placeholder div during lazy loading
  if (!inView && !priority) {
    return (
      <Box
        ref={imgRef}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          aspectRatio,
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx
        }}
        {...props}
      >
        <Box
          sx={{
            width: '40px',
            height: '40px',
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            opacity: 0.5
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={imgRef}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio,
        backgroundColor: '#f0f0f0',
        ...sx
      }}
      {...props}
    >
      <Box
        component="img"
        src={error ? '/placeholder-image.jpg' : imageUrl}
        alt={alt}
        sizes={sizes}
        decoding="async"
        fetchPriority={priority ? "high" : "low"}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.2s ease-out',
          position: 'absolute',
          top: 0,
          left: 0
        }}
      />
      
      {/* Simple loading indicator */}
      {!isLoaded && !error && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '24px',
            height: '24px',
            border: '2px solid #e0e0e0',
            borderTop: '2px solid #1976d2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
              '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
            }
          }}
        />
      )}
    </Box>
  );
};

export default FastImage;
